/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { useCallback, useRef } from 'react';
import NextLink from 'next/link';
import { tv } from 'tailwind-variants';
import { SvgIcon } from 'helpers/SvgIcon';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
// Local
import useDictionary from 'lib/hooks/useDictionary';
import { useRealPathName } from 'lib/hooks/useRealPathName';
import { useHeader } from './HeaderContext';
import { capitalizeFirstLetter } from 'lib/utils/string-utils';
import { getTestProps } from 'lib/testing/utils';
import { US, AE, MX, CA, BR } from 'country-flag-icons/react/3x2';
const Flags = {
    US,
    AE,
    MX,
    CA,
    BR
};
// Create region name formatter
const regionNameFormatter = new Intl.DisplayNames([
    'en'
], {
    type: 'region'
});
const LanguageSelector = ({ regionList })=>{
    const locale = process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';
    const { isMobile, isMobileLanguageSelectorOpen, isDesktopLanguageSelectorOpen, setMobileLanguageSelectorOpen, setDesktopLanguageSelectorOpen, closeDesktopMenus, closeMobileMenu } = useHeader();
    // Don't use from router because that includes rewrites
    const pathname = useRealPathName();
    const selectRef = useRef(null);
    const dropdownRef = useRef(null);
    const { getDictionaryValue } = useDictionary();
    const { sitecoreContext: layoutSitecoreContext } = useSitecoreContext();
    const languages = layoutSitecoreContext?.languages ?? [];
    const currentLanguage = languages.find((x)=>x.isoCode === locale);
    // Helper function to parse language and country from nativeName
    const parseLanguageAndCountry = (nativeName, countryCode)=>{
        // For English, add United States if not present
        if (countryCode === 'US' && !nativeName.includes('United States')) {
            return {
                language: nativeName,
                country: 'United States'
            };
        }
        // For other languages, parse the nativeName
        const match = nativeName.match(/(.*?)\s*\((.*?)\)/);
        if (match) {
            return {
                language: match[1].trim(),
                country: match[2].trim()
            };
        }
        return {
            language: nativeName,
            country: regionNameFormatter.of(countryCode.toUpperCase())
        };
    };
    // Gets the language and country information for a given language object, with the language name capitalized
    const getCurrentLanguageInfo = useCallback((language)=>{
        const countryCode = language?.countryCode;
        const { language: langName, country } = parseLanguageAndCountry(language?.nativeName || '', countryCode || '');
        return {
            language: capitalizeFirstLetter(langName),
            country
        };
    }, []);
    const { language, country } = getCurrentLanguageInfo(currentLanguage);
    // Flag component based off country code
    const CurrentFlag = Flags[currentLanguage?.countryCode];
    const isOpen = isMobile ? isMobileLanguageSelectorOpen : isDesktopLanguageSelectorOpen;
    const setIsOpen = isMobile ? setMobileLanguageSelectorOpen : setDesktopLanguageSelectorOpen;
    const closeAllMenus = isMobile ? closeMobileMenu : closeDesktopMenus;
    // Helper function to check if a language is available in a region
    const isLanguageAvailableInRegion = (region)=>{
        return region.fields?.languageList?.some((lang)=>{
            const regionalIsoCode = lang.fields?.['Regional Iso Code']?.value;
            return languages.some((sitecoreLang)=>sitecoreLang.isoCode === regionalIsoCode || sitecoreLang.isoCode.startsWith(regionalIsoCode + '-') || regionalIsoCode.startsWith(sitecoreLang.isoCode + '-'));
        });
    };
    /*
   * Event Handlers
   */ const handleKeyDown = useCallback((event)=>{
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(!isOpen);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
            closeAllMenus();
        }
    }, [
        isOpen,
        setIsOpen,
        closeAllMenus
    ]);
    const handleBlur = useCallback((event)=>{
        // Only apply blur behavior on desktop
        if (!isMobile) {
            // Close if focus moves to any element outside the language selector
            if (event.relatedTarget !== null && !selectRef.current?.contains(event.relatedTarget) && !dropdownRef.current?.contains(event.relatedTarget)) {
                setIsOpen(false);
                closeAllMenus();
            }
        }
    }, [
        closeAllMenus,
        setIsOpen,
        isMobile
    ]);
    const handleClick = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    }, [
        isOpen,
        setIsOpen
    ]);
    /*
   * Rendering
   */ const { base, buttonClasses, imageContainer, countryNameWrapper, dropDownMenuWrapper, dropDownMenuList, dropDownImageWrapper, dropDownItemName, dropDownMenuColHeading, instructions, buttonIcon, countryText, languageText, gridContainer, gridWrapper, gridSection, flagIcon, chevronContainer, checkmarkIcon } = TAILWIND_VARIANTS({
        isOverlayVisible: isOpen
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: base(),
                ref: selectRef,
                ...getTestProps(`component-header-language-selector`),
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        "aria-expanded": isOpen,
                        "aria-haspopup": "true",
                        "aria-controls": isOpen ? 'language-selector-dropdown' : undefined,
                        "aria-label": `${getDictionaryValue('CountrySelectorAriaLabel') || 'Select A Country'} - ${country} ${language}`,
                        className: buttonClasses(),
                        onClick: handleClick,
                        onKeyDown: handleKeyDown,
                        onBlur: handleBlur,
                        type: "button",
                        role: "menuitem",
                        ...getTestProps(`select-button`),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: imageContainer(),
                            children: [
                                CurrentFlag && /*#__PURE__*/ _jsx(CurrentFlag, {
                                    width: 28,
                                    height: 20,
                                    className: flagIcon()
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: countryNameWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "sr-only",
                                            children: getDictionaryValue('CountrySelectorAriaLabel') || 'Select A Country'
                                        }),
                                        /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: countryText({
                                                        isMainButton: true
                                                    }),
                                                    ...getTestProps(`country-text`),
                                                    children: [
                                                        country,
                                                        " ",
                                                        ''
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: languageText({
                                                        isMainButton: true,
                                                        isOverlayVisible: isOpen
                                                    }),
                                                    ...getTestProps(`language-text`),
                                                    children: language
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: chevronContainer(),
                                    ...getTestProps(`chevron`),
                                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                                        className: buttonIcon(),
                                        fill: "currentColor",
                                        icon: "chevron-up",
                                        size: "xs",
                                        viewBox: "0 0 24 24"
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: instructions(),
                        ...getTestProps(`country-selector-instructions`),
                        children: getDictionaryValue('CountrySelectorInstructions') || 'Choose your country and language'
                    })
                ]
            }),
            isOpen && /*#__PURE__*/ _jsx("div", {
                id: "language-selector-dropdown",
                className: dropDownMenuWrapper(),
                role: "group",
                "aria-labelledby": "language-selector-menu",
                ref: dropdownRef,
                ...getTestProps(`language-dropdown`),
                children: /*#__PURE__*/ _jsx("div", {
                    className: gridSection(),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: gridWrapper(),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: gridContainer(),
                            children: regionList?.map((region)=>{
                                // Skip regions with no available languages
                                if (!isLanguageAvailableInRegion(region)) return null;
                                return /*#__PURE__*/ _jsxs("div", {
                                    role: "group",
                                    "aria-labelledby": `language-region-${region.id}`,
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            field: region.fields?.regionName,
                                            tag: "h2",
                                            className: dropDownMenuColHeading(),
                                            id: `language-region-${region.id}`,
                                            ...getTestProps(`language-region-${region.id}`)
                                        }),
                                        /*#__PURE__*/ _jsx("ul", {
                                            className: dropDownMenuList(),
                                            role: "presentation",
                                            children: region.fields?.languageList?.map((language)=>{
                                                const regionalIsoCode = language.fields?.['Regional Iso Code']?.value;
                                                const matchedLanguage = languages.find((lang)=>{
                                                    const matches = lang.isoCode === regionalIsoCode || lang.isoCode.startsWith(regionalIsoCode + '-') || regionalIsoCode.startsWith(lang.isoCode + '-');
                                                    return matches;
                                                });
                                                if (!matchedLanguage) return null;
                                                const isSelected = currentLanguage?.isoCode === matchedLanguage.isoCode;
                                                const { dropDownMenuItem } = TAILWIND_VARIANTS({
                                                    isSelected
                                                });
                                                const countryCode = regionalIsoCode?.split('-')[1];
                                                const CurrentFlag = Flags[countryCode];
                                                if (!CurrentFlag) {
                                                    console.error(`Include the country: ${countryCode} in the Flags Array.`);
                                                }
                                                const { language: langName, country: countryName } = getCurrentLanguageInfo(matchedLanguage);
                                                const ariaLabelText = `${countryName} ${langName}`;
                                                return /*#__PURE__*/ _jsx("li", {
                                                    role: "presentation",
                                                    onBlur: handleBlur,
                                                    className: dropDownMenuItem(),
                                                    ...getTestProps(`header-language-dropdown-item`),
                                                    children: /*#__PURE__*/ _jsxs(NextLink, {
                                                        href: pathname,
                                                        locale: matchedLanguage.isoCode,
                                                        className: dropDownImageWrapper(),
                                                        "aria-current": isSelected ? 'true' : undefined,
                                                        "aria-label": isSelected ? `${ariaLabelText} ${getDictionaryValue('IsSelected') || 'is selected'}` : ariaLabelText,
                                                        onClick: ()=>{
                                                            closeAllMenus();
                                                            setIsOpen(false);
                                                        },
                                                        ...getTestProps(`dropdown-link-${language.id}`),
                                                        children: [
                                                            CurrentFlag && /*#__PURE__*/ _jsx(CurrentFlag, {
                                                                width: 28,
                                                                height: 20,
                                                                className: flagIcon(),
                                                                ...getTestProps(`flag-icon`)
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: dropDownItemName(),
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        className: countryText({
                                                                            isSelected
                                                                        }),
                                                                        children: [
                                                                            countryName,
                                                                            " ",
                                                                            ''
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("span", {
                                                                        className: languageText({
                                                                            isSelected
                                                                        }),
                                                                        children: langName
                                                                    }),
                                                                    isSelected && /*#__PURE__*/ _jsx("span", {
                                                                        className: "sr-only",
                                                                        children: getDictionaryValue('IsSelected') || 'is selected'
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx(SvgIcon, {
                                                                icon: "checkmark",
                                                                size: "s",
                                                                viewBox: "0 0 24 24",
                                                                className: checkmarkIcon({
                                                                    isSelected
                                                                })
                                                            })
                                                        ]
                                                    })
                                                }, language.id);
                                            })
                                        })
                                    ]
                                }, region.id);
                            })
                        })
                    })
                })
            })
        ]
    });
};
export default LanguageSelector;
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'block',
            'relative',
            'text-left',
            'md:mr-4'
        ],
        buttonClasses: [
            'block',
            'w-full',
            'px-spacing-spacing-12',
            'py-spacing-spacing-8',
            'md:p-xxs',
            'rounded-md',
            'border-gray-300',
            'text-base',
            'sm:text-sm',
            'cursor-pointer',
            'focus:outline-none',
            'focus:ring-4',
            'focus:ring-color-border-border-focus',
            'hover:bg-color-surface-surface-secondary',
            'group',
            'transition-colors',
            'duration-200'
        ],
        imageContainer: [
            'flex',
            'items-center',
            'w-full',
            'justify-between',
            'md:justify-start',
            'hover:text-color-text-text'
        ],
        countryNameWrapper: [
            'flex',
            'flex-col',
            'ml-spacing-spacing-12',
            'mr-5',
            'truncate',
            'lg:flex-col',
            'capitalize',
            'w-full',
            'md:w-auto',
            'text-left'
        ],
        countryText: [
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-tight',
            'text-color-text-text-secondary',
            'group-hover:text-color-text-text',
            'group-focus:text-color-text-text'
        ],
        languageText: [
            'text-typography-font-size-body-small',
            'font-normal',
            'leading-tight',
            'text-color-text-text-secondary',
            'group-hover:text-color-text-text',
            'group-focus:text-color-text-text'
        ],
        dropDownMenuWrapper: [
            'absolute',
            'left-0',
            'right-0',
            'bg-white',
            'z-10',
            'top-full',
            'border-b',
            'border-color-border-light',
            'overflow-hidden',
            'transition-all',
            'duration-300',
            'ease-in-out',
            'md:opacity-100',
            'md:block',
            'max-h-0',
            'opacity-0',
            'relative',
            'md:absolute'
        ],
        dropDownMenuList: [
            'flex',
            'flex-col',
            'list-none',
            'py-2',
            'gap-spacing-spacing-8'
        ],
        dropDownMenuColHeading: [
            'font-semibold',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary',
            'text-sm'
        ],
        dropDownMenuItem: [
            'cursor-pointer',
            'list-none',
            'relative',
            'select-none'
        ],
        dropDownImageWrapper: [
            'flex',
            'items-center',
            'justify-start',
            'w-full',
            'px-spacing-spacing-12',
            'py-spacing-spacing-8',
            'rounded-general-border-radius-button',
            'border',
            '!border-transparent',
            'border-width-025',
            'group',
            'hover:bg-color-fill-fill-hover',
            'hover:!border-color-border-border-hover',
            'focus:bg-color-fill-fill-hover',
            'focus:!border-color-border-border-hover',
            'focus:outline-none',
            'focus:ring-4',
            'focus:ring-color-border-border-focus'
        ],
        dropDownItemName: [
            'block',
            'font-semibold',
            'ml-3',
            'capitalize',
            'flex',
            'flex-col'
        ],
        gridContainer: [
            'grid',
            'w-full',
            'grid-cols-1',
            'md:grid-cols-2',
            'lg:grid-cols-3',
            'xl:grid-cols-4',
            'md:gap-8',
            'md:p-8'
        ],
        gridWrapper: [
            'max-w-screen-dimensions-max-width',
            'flex',
            'xl:mx-auto',
            'md:px-general-spacing-margin-x'
        ],
        gridSection: [
            'px-spacing-spacing-8',
            'bg-color-surface-surface',
            'border-b',
            'overflow-hidden',
            'border-t',
            'border-color-border-light'
        ],
        flagIcon: [
            'mt-0.5',
            'self-baseline'
        ],
        chevronContainer: [
            'flex'
        ],
        buttonIcon: [
            'duration-200',
            'h-4',
            'w-4',
            'transition'
        ],
        checkmarkIcon: [
            'ml-auto',
            'opacity-0',
            'transition-opacity',
            'duration-200',
            'text-color-icon-icon'
        ],
        instructions: [
            'px-spacing-spacing-8',
            'py-spacing-spacing-16',
            'font-bold',
            'text-left',
            'md:hidden'
        ]
    },
    variants: {
        isSelected: {
            false: {
                dropDownMenuItem: [],
                checkmarkIcon: [
                    'group-hover:opacity-100',
                    'group-focus:opacity-100'
                ],
                countryText: [
                    'text-color-text-text-secondary',
                    'group-hover:text-color-text-text'
                ],
                languageText: [
                    'text-color-text-text-secondary',
                    'group-hover:text-color-text-text'
                ]
            },
            true: {
                dropDownMenuItem: [],
                checkmarkIcon: [
                    'opacity-100',
                    'text-color-icon-icon-success',
                    'group-hover:opacity-100',
                    'group-focus:opacity-100'
                ],
                countryText: [
                    'text-color-text-text'
                ],
                languageText: [
                    'text-color-text-text'
                ]
            }
        },
        isMainButton: {
            true: {
                countryText: [
                    'group-hover:!text-color-text-text'
                ],
                languageText: [
                    'group-hover:!text-color-text-text'
                ]
            },
            false: {}
        },
        isOverlayVisible: {
            true: {
                overlay: [
                    'block'
                ],
                dropDownMenuWrapper: [
                    'md:block',
                    'max-h-screen',
                    'opacity-100',
                    'relative',
                    'md:absolute',
                    'overflow-y-auto'
                ],
                buttonIcon: [
                    'rotate-0',
                    'text-color-icon-icon-selected'
                ]
            },
            false: {
                overlay: [
                    'hidden'
                ],
                dropDownMenuWrapper: [
                    'md:hidden',
                    'max-h-0',
                    'opacity-0',
                    'relative',
                    'md:absolute'
                ],
                buttonIcon: [
                    'rotate-180',
                    'text-color-icon-icon'
                ]
            }
        }
    },
    compoundVariants: [
        {
            isMainButton: true,
            isOverlayVisible: true,
            class: {
                countryText: [
                    'text-color-text-text'
                ],
                languageText: [
                    'text-color-text-text'
                ]
            }
        }
    ]
});
